import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Calendar, Store } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';
import Text from 'components/_UI/Text';
import Icon from 'components/_UI/Icon';
import Progress from 'components/_UI/Progress';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import { FullyCentered } from 'styled/alignment/Center';
import ItemPerformanceCard from './ItemPerformanceCard';
import { summarizeTicket, percentOf, formatInt } from './salesTokens';

const SORTS = [
    { id: 'sold', label: 'Mais vendidos' },
    { id: 'revenue', label: 'Maior receita' },
    { id: 'name', label: 'Nome' },
];

/**
 * Painel de vendas compartilhado pelas abas Ingressos e Produtos.
 *
 * O bloco de estatísticas do topo é o mesmo da develop, mantido intencionalmente.
 * O que foi substituído são as antigas seções "Estatísticas de Vendas por
 * Categoria" e a tabela de tipos — que repetiam a mesma lista em dois formatos
 * incompletos — pela seção única "Desempenho por ingresso/produto".
 */
export default function SalesPanel({
    items = [],
    totals,
    channels = null,
    copy,
}) {
    const [sort, setSort] = useState('sold');

    const stats = useMemo(() => {
        const sold = Number(totals?.sold) || 0;
        const total = Number(totals?.total) || 0;
        const revenueSold = Number(totals?.revenueSold) || 0;
        const revenuePotential = Number(totals?.revenuePotential) || 0;

        const percentSold = percentOf(sold, total);

        return {
            sold,
            total,
            available: Math.max(total - sold, 0),
            revenueSold,
            revenuePotential,
            percentSold,
            percentAvailable: 100 - percentSold,
            percentRevenue: percentOf(revenueSold, revenuePotential),
        };
    }, [totals]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const sa = summarizeTicket(a);
            const sb = summarizeTicket(b);

            if (sort === 'revenue') return sb.collected - sa.collected;
            if (sort === 'name') return (a?.name || '').localeCompare(b?.name || '', 'pt-BR');
            return sb.sold - sa.sold;
        });
    }, [items, sort]);

    /* Campeão = maior arrecadação. Só faz sentido destacar quando há mais de um
       item e alguém de fato vendeu. */
    const topSellerId = useMemo(() => {
        if (items.length < 2) return null;

        const best = items.reduce(
            (acc, el) => {
                const collected = summarizeTicket(el).collected;
                return collected > acc.collected ? { id: el.id, collected } : acc;
            },
            { id: null, collected: 0 }
        );

        return best.collected > 0 ? best.id : null;
    }, [items]);

    /* Venda no PDV não tem lote: ela existe no total do evento e em nenhuma
       linha da lista abaixo. Somada sem rótulo aos cards do topo, vira chamado
       de conferência — o produtor confere lote a lote e sempre falta dinheiro.
       Por isso os cards mostram o online, que fecha com a lista, e o presencial
       aparece aqui com o total geral ao lado. */
    const offline = useMemo(() => {
        const sold = Number(channels?.soldOffline) || 0;
        const revenue = Number(channels?.revenueOffline) || 0;

        return {
            sold,
            revenue,
            hasSales: sold > 0 || revenue > 0,
            totalSold: stats.sold + sold,
            totalRevenue: stats.revenueSold + revenue,
        };
    }, [channels, stats.sold, stats.revenueSold]);

    return (
        <Container>
            {/* ───────── Estatísticas do topo (mantidas da develop) ───────── */}
            <TicketsStatistics>
                <StatisticCardItem type='sold'>
                    <Column>
                        <Text color={colors.secondaryPurple} paddingBottom={'0'}>
                            {copy.soldLabel}
                        </Text>
                        <StyledProgressSale>
                            <Row style={{ alignItems: 'flex-end' }}>
                                <Text color={colors.secondaryPurple} name='hero' paddingBottom={0}>
                                    {stats.sold}/
                                </Text>
                                <Text color={colors.secondaryPurple} name='title' paddingBottom={'8px'}>
                                    {stats.total}
                                </Text>
                            </Row>
                            <CircularIcon type='sold'>
                                <Column width='20px' height='20px'>
                                    <Icon name={copy.iconName} color={colors.neutralLight} />
                                </Column>
                            </CircularIcon>
                        </StyledProgressSale>
                    </Column>

                    <Column marginTop='16px'>
                        <Progress
                            value={Math.round(stats.percentSold)}
                            bgColor={'#E9D5FF'}
                            color={colors.secondaryPurple}
                        />
                        <Text color={colors.secondaryPurple} marginTop={'6px'}>
                            {Math.round(stats.percentSold)}% total
                        </Text>
                    </Column>
                </StatisticCardItem>

                <StatisticCardItem type='toSell'>
                    <Column>
                        <Text color={colors.success} paddingBottom={'0'}>
                            Disponíveis
                        </Text>
                        <ContentTicketToSell>
                            <Row style={{ alignItems: 'flex-end' }}>
                                <Text color={colors.success} name='hero' paddingBottom={'0'}>
                                    {stats.available}/
                                </Text>
                                <Text color={colors.success} name='title' paddingBottom={'8px'}>
                                    {stats.total}
                                </Text>
                            </Row>
                            <CircularIcon type='toSell'>
                                <Column width='24px' height='24px'>
                                    <Icon name={copy.iconName} color={colors.neutralLight} />
                                </Column>
                            </CircularIcon>
                        </ContentTicketToSell>
                    </Column>

                    <Column marginTop='16px'>
                        <Progress
                            value={Math.round(stats.percentAvailable)}
                            bgColor={'#BBF7D0'}
                            color={colors.success}
                        />
                        <Text marginTop={'6px'} color={colors.success}>
                            {Math.round(stats.percentAvailable)}% disponivel
                        </Text>
                    </Column>
                </StatisticCardItem>

                <StatisticCardItem type='general'>
                    <Column>
                        <Text color={colors.primary} paddingBottom={'0'}>
                            Receita total
                        </Text>
                        <ContentTicketToSell>
                            <Column>
                                <Text color={colors.primaryDark} paddingBottom={'0'} name='title'>
                                    {currency.format(stats.revenueSold)}
                                </Text>
                                <Text color={colors.primary} paddingBottom={'0'}>
                                    de {currency.format(stats.revenuePotential)}
                                </Text>
                            </Column>
                            <CircularIcon type='general'>
                                <Calendar color={colors.neutralLight} />
                            </CircularIcon>
                        </ContentTicketToSell>
                    </Column>

                    <Column marginTop='16px'>
                        <Progress
                            value={Math.round(stats.percentRevenue)}
                            bgColor={'#BFDBFE'}
                            color={colors.primary}
                        />
                        <Text color={colors.primary} marginTop={'6px'}>
                            {Math.round(stats.percentRevenue)}% da meta
                        </Text>
                    </Column>
                </StatisticCardItem>
            </TicketsStatistics>

            {/* ───────── Desempenho item a item ─────────
                Substitui as antigas "Estatísticas de Vendas por Categoria" e a
                tabela de tipos, que repetiam a mesma lista em dois formatos
                incompletos: as barras não traziam denominador e a tabela gastava
                metade da largura para mostrar só nome e status. */}
            <Section>
                <SectionHead>
                    <Column width='auto'>
                        <SectionTitle>{copy.sectionTitle}</SectionTitle>
                        <SectionSubtitle>{copy.sectionSubtitle}</SectionSubtitle>
                    </Column>

                    {sortedItems.length > 1 && (
                        <SortGroup role='group' aria-label={copy.sortLabel}>
                            {SORTS.map((option) => (
                                <SortButton
                                    key={option.id}
                                    type='button'
                                    $active={sort === option.id}
                                    aria-pressed={sort === option.id}
                                    onClick={() => setSort(option.id)}
                                >
                                    {option.label}
                                </SortButton>
                            ))}
                        </SortGroup>
                    )}
                </SectionHead>

                {sortedItems.length > 0 ? (
                    <ItemList>
                        {sortedItems.map((el) => (
                            <ItemPerformanceCard
                                key={el.id}
                                item={el}
                                hiddenLabel={copy.hiddenLabel}
                                isTopSeller={el.id === topSellerId}
                            />
                        ))}
                    </ItemList>
                ) : (
                    <EmptyState>{copy.emptyText}</EmptyState>
                )}
            </Section>

            {offline.hasSales && (
                <ChannelBlock>
                    <ChannelHead>
                        <Store size={13} strokeWidth={2.5} aria-hidden='true' />
                        Vendas no caixa presencial (PDV)
                    </ChannelHead>

                    <ChannelFigures>
                        <ChannelFigure>
                            <ChannelLabel>{copy.soldLabel}</ChannelLabel>
                            <ChannelValue>{formatInt(offline.sold)}</ChannelValue>
                        </ChannelFigure>

                        <ChannelFigure>
                            <ChannelLabel>Arrecadado</ChannelLabel>
                            <ChannelValue>{currency.format(offline.revenue)}</ChannelValue>
                        </ChannelFigure>

                        <ChannelFigure>
                            <ChannelLabel>Total geral (online + PDV)</ChannelLabel>
                            <ChannelValue $strong>
                                {formatInt(offline.totalSold)}
                                <ChannelValueDim> · {currency.format(offline.totalRevenue)}</ChannelValueDim>
                            </ChannelValue>
                        </ChannelFigure>
                    </ChannelFigures>

                    <ChannelHint>
                        O caixa presencial vende sem lote, então estas vendas não entram nos cards do
                        topo nem na lista de desempenho — os números acima são das vendas online.
                    </ChannelHint>
                </ChannelBlock>
            )}
        </Container>
    );
}

/* ─────────────────────────────  Layout  ───────────────────────────── */

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    animation: ${fadeUp} 0.3s ease both;
`;

/* ── Estatísticas do topo — preservadas da develop ── */

const TicketsStatistics = styled(Column)`
    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 12px;
    }
`;

const StatisticCardItem = styled(Column)`
    margin-bottom: 14px;
    width: 100%;
    padding: 14px;
    justify-content: space-between;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 32.5%;
    }

    ${({ type }) => type == 'sold' && `
        background-color: #F7EFFF;
        border: 1px solid ${colors.secondaryPurple};
        border-radius: 8px;
    `};

    ${({ type }) => type == 'toSell' && `
        border: 1px solid ${colors.success};
        border-radius: 8px;
    `};

    ${({ type }) => type == 'general' && `
        background-color: #E6F1FE;
        border: 1px solid ${colors.primary};
        border-radius: 8px;
    `};
`;

const CircularIcon = styled(FullyCentered)`
    border-radius: 30px;
    width: 50px;
    padding: 8px;

    ${({ type }) => type == 'sold' && `
        background-color: ${colors.secondaryPurple};
    `};

    ${({ type }) => type == 'toSell' && `
        background-color: ${colors.success};
    `};

    ${({ type }) => type == 'general' && `
        background-color: ${colors.primary};
    `};
`;

const StyledProgressSale = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const ContentTicketToSell = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

/* ── Seção de desempenho ── */

const Section = styled.section`
    margin-top: 8px;
    padding: 20px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const SectionHead = styled.header`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const SectionSubtitle = styled.p`
    margin: 2px 0 0;
    font-size: 12px;
    color: ${colors.night};
`;

const SortGroup = styled.div`
    display: inline-flex;
    max-width: 100%;
    padding: 3px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    flex-shrink: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const SortButton = styled.button`
    padding: 6px 12px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    background: ${({ $active }) => ($active ? colors.neutralLight : 'transparent')};
    color: ${({ $active }) => ($active ? colors.nightDark : colors.night)};
    box-shadow: ${({ $active }) => ($active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none')};

    &:hover {
        color: ${colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${colors.secondaryPurple};
        outline-offset: 2px;
    }
`;

const ItemList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const EmptyState = styled.p`
    margin: 0;
    padding: 28px 0;
    text-align: center;
    font-size: 13px;
    color: ${colors.night};
`;

/* ── Recorte de canal (PDV) ── */

const ChannelBlock = styled.section`
    margin: 12px 0 8px;
    padding: 14px 16px;
    border-radius: 12px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(16, 24, 40, 0.1);
`;

const ChannelHead = styled.h3`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 10px;
    font-size: 12px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const ChannelFigures = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 18px;

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`;

const ChannelFigure = styled.div`
    min-width: 0;
`;

const ChannelLabel = styled.span`
    display: block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.night};
`;

const ChannelValue = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 14px;
    font-weight: ${({ $strong }) => ($strong ? 800 : 600)};
    color: ${colors.nightDark};
`;

const ChannelValueDim = styled.span`
    font-weight: 600;
    color: ${colors.night};
`;

const ChannelHint = styled.p`
    margin: 10px 0 0;
    font-size: 11px;
    color: ${colors.night};
`;
